import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { Product } from '@/types';

const Products: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();

  // Filter state
  const [filters, setFilters] = useState({
    sku: '',
    date_updated_from: '',
    date_updated_to: '',
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    perPage: 25,
  });

  // Sorting state
  const [sorting, setSorting] = useState({
    sortBy: 'id',
    sortOrder: 'desc' as 'asc' | 'desc',
  });

  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState<{ objects: Product[], total: number, page: number } | null>(null);

  // Fetch products
  const { data: productsData, isLoading, error } = useQuery(
    ['products', pagination.currentPage, sorting.sortBy, sorting.sortOrder],
    () => apiService.getProducts({
      order_by: sorting.sortBy,
      order_type: sorting.sortOrder,
      page: pagination.currentPage,
    }),
    {
      onSuccess: (data) => {
        setPagination(prev => ({
          ...prev,
          totalPages: Math.ceil(data.total / prev.perPage),
        }));
      },
    }
  );

  // Extract products from paginated response
  const products = isSearchActive && searchResults?.objects ? searchResults.objects : (productsData?.objects || []);

  // Create product mutation
  const productMutation = useMutation(
    (productData: Partial<Product>) => {
      return apiService.createProduct(productData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
        setShowModal(false);
      },
    }
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const productData: Partial<Product> = {
      sku: formData.get('sku') as string,
      title: formData.get('title') as string,
      price: parseFloat(formData.get('price') as string),
      disposable_quantity: parseInt(formData.get('disposable_quantity') as string),
    };

    productMutation.mutate(productData);
  };

  const handleSearch = async () => {
    const searchParams = {
      ...filters,
      order_by: sorting.sortBy,
      order_type: sorting.sortOrder,
      page: 1,
    };

    try {
      const results = await apiService.searchProducts(searchParams);
      setSearchResults(results);
      setIsSearchActive(true);
      setPagination(prev => ({
        ...prev,
        currentPage: 1,
        totalPages: Math.ceil(results.total / prev.perPage),
      }));
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleClearSearch = () => {
    setIsSearchActive(false);
    setSearchResults(null);
    setFilters({
      sku: '',
      date_updated_from: '',
      date_updated_to: '',
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleSort = (column: string) => {
    setSorting(prev => ({
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">
              Failed to load products. Please try again.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Products</h1>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
          style={{ backgroundColor: 'hsl(15 100% 52%)' }}
        >
          <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
          <span>Add Product</span>
        </button>
      </div>

      {/* Search Filters */}
      <div className="card">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="form-label">SKU</label>
              <input
                type="text"
                value={filters.sku}
                onChange={(e) => setFilters(prev => ({ ...prev, sku: e.target.value }))}
                className="form-input"
                placeholder="Search by SKU"
              />
            </div>
            <div>
              <label className="form-label">Date Updated From</label>
              <input
                type="date"
                value={filters.date_updated_from}
                onChange={(e) => setFilters(prev => ({ ...prev, date_updated_from: e.target.value }))}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Date Updated To</label>
              <input
                type="date"
                value={filters.date_updated_to}
                onChange={(e) => setFilters(prev => ({ ...prev, date_updated_to: e.target.value }))}
                className="form-input"
              />
            </div>
            <div className="flex items-end space-x-2">
              <button
                onClick={handleSearch}
                className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center space-x-2"
                style={{ backgroundColor: 'hsl(15 100% 52%)' }}
              >
                <FontAwesomeIcon icon={faSearch} className="w-4 h-4" />
                <span>Search</span>
              </button>
              {isSearchActive && (
                <button
                  onClick={handleClearSearch}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center space-x-2"
                >
                  <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                  <span>Clear</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th
                  className="cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('id')}
                >
                  ID {sorting.sortBy === 'id' && (sorting.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('sku')}
                >
                  SKU {sorting.sortBy === 'sku' && (sorting.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('title')}
                >
                  Title {sorting.sortBy === 'title' && (sorting.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('price')}
                >
                  Price {sorting.sortBy === 'price' && (sorting.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('disposable_quantity')}
                >
                  Disposable Quantity {sorting.sortBy === 'disposable_quantity' && (sorting.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('datetime_created')}
                >
                  Created {sorting.sortBy === 'datetime_created' && (sorting.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('datetime_last_updated')}
                >
                  Last Updated {sorting.sortBy === 'datetime_last_updated' && (sorting.sortOrder === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id}>
                  <td>{product.id}</td>
                  <td>{product.sku}</td>
                  <td>{product.title}</td>
                  <td>{product.price.toFixed(2)}</td>
                  <td>{product.disposable_quantity}</td>
                  <td>{formatDateTime(product.datetime_created)}</td>
                  <td>{formatDateTime(product.datetime_last_updated)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="flex items-center text-sm text-gray-700">
              <span>
                Showing page {pagination.currentPage} of {pagination.totalPages}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm">
                {pagination.currentPage}
              </span>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                New product
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="form-label">SKU</label>
                  <input
                    type="text"
                    name="sku"
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    name="title"
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Price</label>
                  <input
                    type="number"
                    step="0.01"
                    name="price"
                    className="form-input"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Disposable Quantity</label>
                  <input
                    type="number"
                    name="disposable_quantity"
                    className="form-input"
                    required
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={productMutation.isLoading}
                    className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
                    style={{ backgroundColor: 'hsl(15 100% 52%)' }}
                  >
                    {productMutation.isLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
